import { API_CONFIG, apiUtils } from '../config.js';

// Results Management API - Complete Integration
export const resultApi = {
  // ============================================
  // SINGLE RESULT OPERATIONS
  // ============================================

  // Create single result
  createResult: async (resultData) => {
    const url = `${API_CONFIG.BASE_URL}/results`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(resultData),
    });
  },

  // Get all results with filters
  getAllResults: async (filters = {}) => {
    const queryParams = apiUtils.buildQueryString(filters);
    const url = `${API_CONFIG.BASE_URL}/results?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get single result by ID
  getResultById: async (resultId) => {
    const url = `${API_CONFIG.BASE_URL}/results/${resultId}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Update result
  updateResult: async (resultId, resultData) => {
    const url = `${API_CONFIG.BASE_URL}/results/${resultId}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'PATCH',
      body: JSON.stringify(resultData),
    });
  },

  // Delete result
  deleteResult: async (resultId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_CONFIG.BASE_URL}/results/${resultId}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      return text ? JSON.parse(text) : { success: true };
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // ============================================
  // BULK OPERATIONS
  // ============================================

  // Download Excel template
  downloadTemplate: async (examId, unitId = null, sectionId = null, subjectId = null) => {
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({ examId });
      if (unitId) params.append('unitId', unitId);
      if (sectionId) params.append('sectionId', sectionId);
      if (subjectId) params.append('subjectId', subjectId);

      const response = await fetch(`${API_CONFIG.BASE_URL}/results/bulk/template?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `result_template_${examId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // Upload bulk results
  uploadBulkResults: async (file, examId, sectionId = null) => {
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('examId', examId);
      if (sectionId) formData.append('sectionId', sectionId);

      const response = await fetch(`${API_CONFIG.BASE_URL}/results/bulk/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Upload failed');
      }

      return await response.json();
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // Publish/Unpublish results
  publishResults: async (examId, status, unitId, sectionId = null) => {
    const url = `${API_CONFIG.BASE_URL}/results/bulk/publish`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        examId,
        unitId,
        status, // 'published' or 'draft'
        ...(sectionId && { sectionId })
      }),
    });
  },

  // Download report cards (PDF)
  downloadReportCards: async (examId, unitId, sectionId = null, studentIds = null) => {
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({ examId, unitId });
      if (sectionId) params.append('sectionId', sectionId);
      if (studentIds && studentIds.length > 0) params.append('studentIds', studentIds.join(','));

      const response = await fetch(`${API_CONFIG.BASE_URL}/results/bulk/reports?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate report cards');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_cards_${examId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // Download class performance report (PDF)
  downloadClassPerformance: async (examId, unitId, sectionId = null) => {
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({ examId, unitId });
      if (sectionId) params.append('sectionId', sectionId);

      const response = await fetch(`${API_CONFIG.BASE_URL}/results/bulk/class-performance?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate performance report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `class_performance_${examId}_${unitId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // ============================================
  // STUDENT-SPECIFIC OPERATIONS
  // ============================================

  // Get student results
  getStudentResults: async (studentRegistrationId, academicYear = null) => {
    const params = academicYear ? `?academicYear=${academicYear}` : '';
    const url = `${API_CONFIG.BASE_URL}/results/student/${studentRegistrationId}${params}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get student summary
  getStudentSummary: async (studentRegistrationId, academicYear = null) => {
    const params = academicYear ? `?academicYear=${academicYear}` : '';
    const url = `${API_CONFIG.BASE_URL}/results/student/${studentRegistrationId}/summary${params}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Download Single Student Report Card PDF
  downloadReportCard: async (studentRegistrationId, examId) => {
    try {
        const token = localStorage.getItem('authToken');
        const params = new URLSearchParams({ examId });
        const url = `${API_CONFIG.BASE_URL}/results/student/${studentRegistrationId}/report-card?${params}`;

        const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
           throw new Error('Failed to download report card');
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `ReportCard_${studentRegistrationId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        
        return true;
    } catch (error) {
        apiUtils.handleError(error);
        throw error;
    }
  },

  // ============================================
  // ANALYTICS & STATISTICS
  // ============================================

  // Send Result Notifications (WhatsApp)
  sendResultNotification: async (payload) => {
    // payload: { examId, unitId, sectionId, targetAudience, scheduledDate }
    const url = `${API_CONFIG.BASE_URL}/result-notifications/send`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  // Get exam-subject results
  getExamSubjectResults: async (examId, subjectId) => {
    const url = `${API_CONFIG.BASE_URL}/results/exam/${examId}/subject/${subjectId}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Calculate Summary & Ranks for an Exam
  calculateExamSummary: async (examId, unitId) => {
    const url = `${API_CONFIG.BASE_URL}/results/calculate-summary`;
    return apiUtils.makeAuthenticatedRequest(url, {
        method: 'POST',
        body: JSON.stringify({ examId, unitId })
    });
  },

  // Get overall statistics
  getStatistics: async (filters = {}) => {
    const queryParams = apiUtils.buildQueryString(filters);
    const url = `${API_CONFIG.BASE_URL}/results/statistics?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // ============================================
  // EXAM ASSIGNMENT VALIDATION
  // ============================================

  // Validate if exam is assigned to a class/section
  validateExamAssignment: async (examId, unitId, sectionId = null) => {
    try {
      const params = new URLSearchParams();
      if (sectionId) params.append('sectionId', sectionId);
      
      const url = `${API_CONFIG.BASE_URL}/results/validate-exam/${examId}/class/${unitId}${params.toString() ? '?' + params.toString() : ''}`;
      return apiUtils.makeAuthenticatedRequest(url);
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // Get all exams assigned to a specific class
  getAssignedExamsForClass: async (unitId, academicYear = null, sectionId = null) => {
    try {
      const params = new URLSearchParams();
      if (academicYear) params.append('academicYear', academicYear);
      if (sectionId) params.append('sectionId', sectionId);
      
      const url = `${API_CONFIG.BASE_URL}/results/assigned-exams/class/${unitId}${params.toString() ? '?' + params.toString() : ''}`;
      return apiUtils.makeAuthenticatedRequest(url);
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // ============================================
  // HELPER METHODS
  // ============================================

  // Build query filters for results
  buildResultFilters: (options = {}) => {
    const filters = {};
    
    // Core filters
    if (options.examId) filters.examId = options.examId;
    if (options.unitId) filters.unitId = options.unitId;
    if (options.sectionId) filters.sectionId = options.sectionId;
    if (options.studentRegistrationId) filters.studentRegistrationId = options.studentRegistrationId;
    if (options.subjectId) filters.subjectId = options.subjectId;
    if (options.subjectName) filters.subjectName = options.subjectName;
    if (options.examType) filters.examType = options.examType;
    if (options.academicYear) filters.academicYear = options.academicYear;
    if (options.semester) filters.semester = options.semester;
    if (options.publishStatus) filters.publishStatus = options.publishStatus;
    if (options.grade) filters.grade = options.grade;
    
    // Date range filters
    if (options.examDateFrom) filters.examDateFrom = options.examDateFrom;
    if (options.examDateTo) filters.examDateTo = options.examDateTo;
    
    // Percentage filters
    if (options.minPercentage !== undefined) filters.minPercentage = options.minPercentage;
    if (options.maxPercentage !== undefined) filters.maxPercentage = options.maxPercentage;
    
    // Pagination
    if (options.page) filters.page = options.page;
    if (options.limit) filters.limit = options.limit;
    
    // Sorting
    if (options.sortBy) filters.sortBy = options.sortBy;
    if (options.sortOrder) filters.sortOrder = options.sortOrder;
    
    return filters;
  },

  // Validate result data before submission
  validateResultData: (data) => {
    const errors = [];
    
    if (!data.studentRegistrationId) errors.push('Student Registration ID is required');
    if (!data.unitId) errors.push('Class/Unit is required');
    if (!data.subjectName) errors.push('Subject name is required');
    if (!data.examType) errors.push('Exam type is required');
    if (!data.examName) errors.push('Exam name is required');
    if (!data.academicYear) errors.push('Academic year is required');
    if (!data.totalMarks || data.totalMarks < 1) errors.push('Total marks must be at least 1');
    if (data.obtainedMarks === undefined || data.obtainedMarks < 0) errors.push('Obtained marks cannot be negative');
    if (data.obtainedMarks > data.totalMarks) errors.push('Obtained marks cannot exceed total marks');
    if (!data.examDate) errors.push('Exam date is required');
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default resultApi;
